import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {DxBaseControlOptions} from "../../domain/ui-options/dx-base-control-options";
import {FormControl} from "@angular/forms";
import {Subscription} from "rxjs";
import {DxComponentErrorMessage} from "../../domain/dx-component-error-message";
import {VALIDATION_ERROR_MESSAGES} from "../../../dx-core/utiles/dx-validators";
import {DxCoreUtils} from "../../../dx-core/utiles/dx-core-utils";


/** Базобвый компонент для всех контролов */
@Component({selector: 'dx-base-control', template: ''})
export class DxBaseControlComponent implements OnInit, OnDestroy {

  private _controlId: string;
  /**
   * Уникальный строковый идентификатор контрола.
   * При помощи него удобно искать нужные виджеты в запросах `@ViewChildren(...)`.
   */
  @Input()
  set controlId(value: string) {
    this._controlId = value;
  }

  get controlId(): string {
    return this._controlId ? this._controlId : DxCoreUtils.getControlId(this.formControlInstance);
  }

  /** Опции контрола.*/
  @Input()
  public options: DxBaseControlOptions;

  /** Свойство доступности */
  @Input()
  isReadOnly = false;

  /** Свойство доступности */
  @Input()
  set isDisabled(value: boolean) {
    this._isDisabled = value;
  }

  /** Свойство видимости */
  @Input()
  set isHidden(value: boolean) {
    this._isHidden = value;
  }

  /** Инстанс для привязки контролов к ангуляровским объектам FormControl, которые реализуют реактивные формы */
  @Input()
  set formControlInstance(value: FormControl) {
    this._formControl = value;
  }

  /** Свойство для передачи в компонент кастомных сообщений об ошибках */
  @Input()
  set errorMessages(value: DxComponentErrorMessage[]) {
    value.forEach(item => {
      this._errorMessages.set(item.validator, item.message);
    })
  }

  /** эмиттер события на изменение значения контрола */
  @Output()
  public change: EventEmitter<any> = new EventEmitter();

  /** эмиттер события на изменение статуса контрола (валидный/ивалидный) */
  @Output()
  public statusChange: EventEmitter<any> = new EventEmitter();

  public hasErrors(): boolean {
    return this._formControl.invalid && (this._formControl.dirty || this._formControl.touched)
  }

  public addCustomErrorMessage(validator, message: string): void {
    this._errorMessages.set(validator, message);
  }

  /** функция возращает -1 для задизабленных компонентов, чтобы их нельзя было активировть по клавише таб */
  public disabledTabIndex(): number | null {
    return this._isDisabled ? -1 : null;
  }

  get formControlInstance(): FormControl {
    return this._formControl;
  }

  /**
   * Логика управления доступностью можеть быть сложной, поэтому доступ
   * к этому полю через геттер.
   */
  protected _isDisabled: boolean = false;

  /**
   * Логика управления видимостью можеть быть сложной, поэтому доступ
   * к этому полю через геттер.
   */
  protected _isHidden: boolean = false;

  protected _errorMessage: string = '';

  /** Инстанс FormControl'a */
  protected _formControl: FormControl;

  /** Массив подписок для корректной отписки */
  protected _subscriptions: Subscription[] = [];

  /** Коллекция сообщений об ошибках валидации */
  protected _errorMessages: Map<string, string> = new Map(VALIDATION_ERROR_MESSAGES);


  constructor() { }


  ngOnInit(): void {
    this._init();
  }

  ngOnDestroy(): void {
    this._destroy();
  }

  get errorMessage(): string {
    return this._errorMessage;
  }

  set errorMessage(value: string) {
    this._errorMessage = value;
  }

  get isDisabled(): boolean {
    return this._isDisabled;
  }

  get isHidden(): boolean {
    return this._isHidden;
  }

  public get controlOptions(): DxBaseControlOptions {
    return this.options;
  }

  /**
   * Базовая версия метода инициализации контрола.
   * Метод должен быть переопределен в потомке
   * ```
   * protected _init(): void {
   *     super._init();
   * }
   * ```
   * и вызываться на хуке `OnInit(...)`.
   */
  protected _init(): void {

    if(this.options) {
      if(!this.controlId) this.controlId = this.options.controlId;
      if (this.options.isDisabled) this._isDisabled = this.options.isDisabled;
      if (this.options.isHidden) this._isHidden = this.options.isHidden;
    } else {
      this.options = new DxBaseControlOptions();
    }

    // Если значение переменной задано, то это означает что оно было
    // передано через инпут. У того приоритет
    if(!this._formControl) {
      this._formControl = new FormControl();
    }
    this._statusChanged();
    this._bind();
  };


  /**
   * Базовая версия метода для регистрации различного рода подписок и обработчиков.
   * Метод должен быть переопределен в потомке
   * ```
   * protected _bind(): void {
   *     super._bind();
   * }
   * ```
   */
  protected _bind(): void {
    let subscription = this._formControl.valueChanges.subscribe(value => {
      this.change.emit(value);
    });
    this._subscriptions.push(subscription);

    subscription = this._formControl.statusChanges.subscribe(status => {
      this._statusChanged();
      this.statusChange.emit(status);
    });
    this._subscriptions.push(subscription);
  };

  /**
   * Базовая версия метода обработки изменения статуса компонента.
   * Если в компоненте возникли ошибки валидации, то данный метод должен сформировать
   * соответствующее сообщение для отображения на экране.
   */

  protected _statusChanged(): void {
    const errors = this._formControl.errors;
    if (errors) {
      // достаем сообщение об ошибке, соответствующее сработавшему валидатору
      const errorKey = Object.keys(errors)[0];
      this.errorMessage = this._errorMessages.get(errorKey);
      if (!this.errorMessage) {
        this.errorMessage = "Ошибка валидации";
      }
    }
  }

  /**
   * отлавливает change с внутренних input-ов и преобразует его в нормальное значение
   */
  public onChange(value: Event|any) {
    if (value instanceof Event) {
      value.stopImmediatePropagation();
      // just set old value
      value = this.formControlInstance.value;
    }
    console.log('onChange', value);
    return this.change.emit(value);
  }

  /**
   * Базовая версия метода очистки ресурсов используемых контролом.
   * Метод должен быть переопределен в потомке
   * ```
   * protected _destroy(): void {
   *     super._destroy();
   * }
   * ```
   * и вызываться на хуке `OnDestroy(...)`.
   */
  protected _destroy(): void {
    this._subscriptions.forEach(item => item.unsubscribe());
  }
}
